import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { FileGroup } from "common/FileUpload";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { addApplicationExpenseMinuteBundle } from "src/apis/controllers/expenses";
import { FileProvider, useFileContext } from "src/context/FilesV2";
import { downloadFile, uploadFiles } from "src/util";

const noopFileAction = async () => {};

const uniqueBundleIds = (ids) => [...new Set(ids.filter(Boolean))];

const getBundleIdsFromResponse = (response) => {
  const payload = response?.data ?? response?.Data ?? response;
  const items = Array.isArray(payload) ? payload : [payload];

  return uniqueBundleIds(
    items.flatMap((item) => [
      item?.bundleId,
      item?.BundleId,
      ...(Array.isArray(item?.bundleIds) ? item.bundleIds : []),
      ...(Array.isArray(item?.BundleIds) ? item.BundleIds : []),
    ]),
  );
};

const getBundleIdsFromGroups = (groups = []) =>
  uniqueBundleIds(
    groups.flatMap((group) =>
      (group?.bundles ?? [])
        .filter((bundle) => (bundle?.files?.length ?? 0) > 0)
        .map((bundle) => bundle?.id),
    ),
  );

const ExpenseMinutesDialogContent = ({
  open,
  applicationId,
  groups,
  onClose,
  onSaved,
}) => {
  const { control, fileGroups } = useFileContext();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    control.initialize(
      (groups ?? []).map((group) => ({
        ...group,
        bundles: [],
      })),
    );
    control.setIsDirty?.(false);
  }, [groups, open]);

  const handleSave = async () => {
    const pendingBundleIds = getBundleIdsFromGroups(fileGroups);
    if (pendingBundleIds.length === 0) {
      enqueueSnackbar("Προσθέστε αρχείο Πρακτικού.", { variant: "warning" });
      return;
    }

    try {
      setIsSaving(true);
      const uploadResponse = await control.uploadFileGroups();
      if (uploadResponse?.status === "error" || uploadResponse?.succeeded === false) {
        throw new Error(uploadResponse?.message ?? "Δεν ανέβηκε το Πρακτικό.");
      }

      const uploadedIds = getBundleIdsFromResponse(uploadResponse);
      const bundleIds = uploadedIds.length > 0 ? uploadedIds : pendingBundleIds;

      for (const bundleId of bundleIds) {
        const response = await addApplicationExpenseMinuteBundle({
          applicationId,
          bundleId,
        });
        if (response?.status === "error" || response?.succeeded === false) {
          throw new Error(
            response?.message ?? "Δεν συνδέθηκε το Πρακτικό με τη ΜΗΔ.",
          );
        }
      }

      control.setIsDirty?.(false);
      enqueueSnackbar("Το Πρακτικό προστέθηκε επιτυχώς.", {
        variant: "success",
      });
      await onSaved?.();
      onClose();
    } catch (error) {
      enqueueSnackbar(error?.message ?? "Δεν αποθηκεύτηκε το Πρακτικό.", {
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Νέο Πρακτικό</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {fileGroups.length === 0 && (
            <Alert severity="warning">
              Δεν βρέθηκε ενεργός τύπος αρχείου για Πρακτικά.
            </Alert>
          )}
          {fileGroups.map((group) => (
            <FileGroup
              key={`new-${group.id}`}
              id={group.id}
              title={group?.title}
              reviewMode={false}
              isReadOnly={false}
              canAdd
              canDelete
              maxSize={6291456}
              maxFiles={1}
              maxBundles={group?.maxBundles}
              fields={group?.fields}
              bundles={group?.bundles}
              templates={group?.templates}
              extraTypeDataKeys={group?.extraTypeDataKeys}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Ακύρωση
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || fileGroups.length === 0}
        >
          Αποθήκευση Πρακτικού
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ExpenseMinutesDialog = (props) => (
  <FileProvider
    downloadFile={(fileId, type) => downloadFile(fileId, type)}
    uploadFiles={(groups) => uploadFiles(groups, null)}
    onDeleteBundle={noopFileAction}
    onDeleteFile={noopFileAction}
  >
    <ExpenseMinutesDialogContent {...props} />
  </FileProvider>
);

export default ExpenseMinutesDialog;
